import { capitalizeFirstLetter } from '@/common/helpers';
import { ERecipientRole } from '@/dealroom/recipients/types/enums/recipient-role';
import { EEidType } from '@/modals/recipient/types/enums/EidType';
import { IDocumentSettings } from '@/types/DocumentSettings';
import { IRecipient } from '@/types/Recipient';

export function arrangeRecipientSignOrder(
  newIndex: number,
  oldIndex: number,
  recipients: IRecipient[],
  drag: boolean
): IRecipient[] {
  const sortedRecipients: IRecipient[] = recipients.sort(
    (a: IRecipient, b: IRecipient) => a.orderNum - b.orderNum
  );
  const movedRecipient: IRecipient = sortedRecipients[oldIndex];
  if (drag) {
    const filteredRecipients: IRecipient[] = sortedRecipients.filter(
      (recipient: IRecipient) => recipient.id !== movedRecipient.id
    );
    filteredRecipients.splice(newIndex, 0, movedRecipient);

    return setRecipientSignOrder(filteredRecipients);
  } else {
    movedRecipient.orderNum = newIndex + 1;
    sortedRecipients.sort((a, b) => a.orderNum - b.orderNum);
    return fixRecipientsGaps(sortedRecipients);
  }
}

export function setRecipientSignOrder(recipients: IRecipient[]): IRecipient[] {
  return recipients.map((recipient: IRecipient, index: number) => ({
    ...recipient,
    orderNum: index + 1,
  }));
}

export function fixRecipientsGaps(recipients: IRecipient[]): IRecipient[] {
  const updatedRecipients: IRecipient[] = [];
  recipients.forEach((recipient, i) => {
    const previousRecipient: IRecipient = recipients[i - 1];
    const previousOrganizedRecipient: IRecipient = updatedRecipients[i - 1];

    if (previousRecipient && recipient.orderNum === previousRecipient.orderNum) {
      updatedRecipients.push({ ...recipient, orderNum: previousOrganizedRecipient.orderNum });
    } else if (previousRecipient) {
      updatedRecipients.push({
        ...recipient,
        orderNum: previousOrganizedRecipient.orderNum + 1,
      });
    } else {
      updatedRecipients.push({ ...recipient, orderNum: i + 1 });
    }
  });
  return updatedRecipients;
}

export function getLabelName({ fullName, firstName, lastName, roleName }: IRecipient): string {
  if (fullName) {
    return fullName;
  }
  if (firstName || lastName) {
    return `${firstName} ${lastName}`;
  }
  return roleName;
}

export function getRecipientDataFromEmail(email: string): IRecipient {
  if (email.indexOf('@') === -1) {
    return null;
  }
  const names: string[] = email
    .split('@')[0]
    .split('.')
    .map(name => capitalizeFirstLetter(name));

  if (!names[0]) {
    return null;
  }
  if (names[0].length === 1) {
    return { email: email };
  }
  const lastNames: string[] = names.slice(1, names.length);
  return {
    firstName: names[0],
    lastName: lastNames.join(' ').trim() || '',
    fullName: `${names.join(' ')}`.trim(),
    email: email,
  };
}

export function getDefaultRecipient(isSigning: boolean, settings: IDocumentSettings): IRecipient {
  return {
    role: isSigning ? ERecipientRole.Signer : ERecipientRole.CC,
    verifyEidType: EEidType.BankId,
    verifyEid: !!settings.isSigningEId && isSigning,
    verifySmsOpen: !!settings.verifySmsOpen && isSigning,
    verifySmsSign: !!settings.verifySmsSign && isSigning,
    verifySms: (!!settings.verifySmsSign || !!settings.verifySmsOpen) && isSigning,
  };
}

export function getRecipientFullName(recipient: IRecipient): string {
  if (!recipient) {
    return '';
  }
  return recipient.fullName || recipient.roleName;
}
